import countriesData from '@/config/vatCountries.json';

const COUNTRIES = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
const EUROPEAN_COUNTRIES = new Set(
  (countriesData?.european_countries ?? []).map((country) => country.trim().toLowerCase())
);

export const TAX_MODES = Object.freeze({
  SELLER_TAX_COLLECTED: "SELLER_TAX_COLLECTED",
  PLATFORM_TAX_COLLECTED: "PLATFORM_TAX_COLLECTED",
  REVERSE_CHARGE: "REVERSE_CHARGE",
  NO_TAX_COLLECTED_NOTICE: "NO_TAX_COLLECTED_NOTICE",
});

export const TAX_NOTICE_CODES = Object.freeze({
  LOCAL_TAX_MAY_APPLY_SELLER_RESPONSIBLE: "LOCAL_TAX_MAY_APPLY_SELLER_RESPONSIBLE",
  IMPORT_DUTIES_MAY_APPLY_ON_DELIVERY: "IMPORT_DUTIES_MAY_APPLY_ON_DELIVERY",
  TAX_COLLECTED_ON_BEHALF_OF_SELLER: "TAX_COLLECTED_ON_BEHALF_OF_SELLER",
  REVERSE_CHARGE_EU_VAT: "REVERSE_CHARGE_EU_VAT",
  SELLER_RESPONSIBLE_TAX_NOT_COLLECTED: "SELLER_RESPONSIBLE_TAX_NOT_COLLECTED",
});

export const ROUTE_TYPES = Object.freeze({
  DOMESTIC: "DOMESTIC",
  EU_EU: "EU_EU",
  CROSS_BORDER_IMPORT: "CROSS_BORDER_IMPORT",
});

function normaliseString(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().toLowerCase();
}

function findCountryCandidate(value) {
  if (!value) {
    return null;
  }
  const normalised = normaliseString(value);
  if (!normalised) {
    return null;
  }
  return (
    COUNTRIES.find(
      (country) =>
        normaliseString(country.name) === normalised
        || normaliseString(country.isoCode) === normalised
    ) ?? null
  );
}

function isCountryInEu(candidate) {
  const match = findCountryCandidate(candidate);
  const name = normaliseString(match?.name ?? candidate);
  return name ? EUROPEAN_COUNTRIES.has(name) : false;
}

function resolveCountryIso(candidate) {
  const match = findCountryCandidate(candidate);
  const iso = normaliseString(match?.isoCode ?? "");
  if (iso && iso.length === 2) {
    return iso;
  }
  const raw = normaliseString(candidate);
  return raw.length === 2 ? raw : "";
}

function areSameCountry(lhs, rhs) {
  if (!lhs || !rhs) {
    return false;
  }
  const lhsIso = resolveCountryIso(lhs);
  const rhsIso = resolveCountryIso(rhs);
  if (lhsIso && rhsIso) {
    return lhsIso === rhsIso;
  }
  return normaliseString(lhs) === normaliseString(rhs);
}

export function resolveRouteType({ buyerCountry, sellerCountry } = {}) {
  if (areSameCountry(buyerCountry, sellerCountry)) {
    return ROUTE_TYPES.DOMESTIC;
  }
  if (isCountryInEu(buyerCountry) && isCountryInEu(sellerCountry)) {
    return ROUTE_TYPES.EU_EU;
  }
  return ROUTE_TYPES.CROSS_BORDER_IMPORT;
}

export function resolveOriginalTaxContext({ buyerCountry, sellerCountry, sellerTaxEnabled } = {}) {
  const buyerInEu = isCountryInEu(buyerCountry);
  const sellerInEu = isCountryInEu(sellerCountry);

  if (buyerInEu && sellerInEu) {
    if (sellerTaxEnabled) {
      return {
        taxMode: TAX_MODES.SELLER_TAX_COLLECTED,
        taxNoticeCode: TAX_NOTICE_CODES.TAX_COLLECTED_ON_BEHALF_OF_SELLER,
      };
    }
    return {
      taxMode: TAX_MODES.NO_TAX_COLLECTED_NOTICE,
      taxNoticeCode: TAX_NOTICE_CODES.SELLER_RESPONSIBLE_TAX_NOT_COLLECTED,
    };
  }

  if (areSameCountry(buyerCountry, sellerCountry)) {
    return {
      taxMode: TAX_MODES.NO_TAX_COLLECTED_NOTICE,
      taxNoticeCode: TAX_NOTICE_CODES.LOCAL_TAX_MAY_APPLY_SELLER_RESPONSIBLE,
    };
  }

  return {
    taxMode: TAX_MODES.NO_TAX_COLLECTED_NOTICE,
    taxNoticeCode: TAX_NOTICE_CODES.IMPORT_DUTIES_MAY_APPLY_ON_DELIVERY,
  };
}

export function hasValidBusinessVat(billingInfo = null) {
  return Boolean(
    billingInfo?.is_business === true
    && billingInfo?.vat_valid === true
    && typeof billingInfo?.vat_id === "string"
    && billingInfo.vat_id.trim()
  );
}

export function shouldShowBusinessPurchaseOverride(billingInfo = null) {
  return hasValidBusinessVat(billingInfo);
}

export function shouldTreatPurchaseAsBusiness({
  billingInfo,
  forceNonBusinessPurchase = false,
} = {}) {
  return hasValidBusinessVat(billingInfo) && forceNonBusinessPurchase !== true;
}

export function qualifiesForEuReverseCharge({
  buyerCountry,
  sellerCountry,
  billingInfo,
  forceNonBusinessPurchase = false,
} = {}) {
  if (!shouldTreatPurchaseAsBusiness({ billingInfo, forceNonBusinessPurchase })) {
    return false;
  }

  if (!buyerCountry || !sellerCountry) {
    return false;
  }

  if (areSameCountry(buyerCountry, sellerCountry)) {
    return false;
  }

  return isCountryInEu(buyerCountry) && isCountryInEu(sellerCountry);
}

export function resolveVatRate({ buyerCountry, sellerCountry } = {}) {
  const buyerMatch = findCountryCandidate(buyerCountry);
  const sellerMatch = findCountryCandidate(sellerCountry);
  const buyerName = normaliseString(buyerMatch?.name ?? buyerCountry);
  const sellerName = normaliseString(sellerMatch?.name ?? sellerCountry);

  const buyerInEU = buyerName ? EUROPEAN_COUNTRIES.has(buyerName) : false;
  const sellerInEU = sellerName ? EUROPEAN_COUNTRIES.has(sellerName) : false;
  const buyerVat = Number(buyerMatch?.vat);

  // Charge VAT when the destination is in the EU, even if the seller is outside the EU.
  if (buyerInEU && Number.isFinite(buyerVat) && buyerVat > 0) {
    return buyerVat / 100;
  }

  // If both parties are in the EU but the destination has no VAT configured, fallback to 0.
  if (!buyerInEU || !sellerInEU) {
    return 0;
  }

  return 0;
}

export function calculateVatAmount({ price, shipping = 0, buyerCountry, sellerCountry } = {}) {
  const numericPrice = Number(price);
  const numericShipping = Number(shipping);
  const base = numericPrice + (Number.isFinite(numericShipping) ? numericShipping : 0);
  if (!Number.isFinite(base) || base <= 0) {
    return 0;
  }
  const rate = resolveVatRate({ buyerCountry, sellerCountry });
  if (!rate) {
    return 0;
  }
  const amount = base * rate;
  return Number.isFinite(amount) ? Number(amount.toFixed(2)) : 0;
}
